<template>
  <div class="form">
    <!-- 工号密码登录表单 -->
    <el-form :model="form" :rules="rules" ref="form" :show-message="true">
      <el-form-item class="company-input">
        <el-autocomplete
          style="width: 100%"
          ref="companyName"
          @change="handleInput"
          v-model="form.companyName"
          :fetch-suggestions="getHistoryCompanyList"
          placeholder="酒店/集团名称"
          @select="chooseCompany"
        >
          <template slot-scope="{ item }">
            <div class="flex-between">
              <span class="name">{{ item.name }}</span>
              <span class="note N4">{{ item.note }}</span>
            </div>
          </template>
        </el-autocomplete>
      </el-form-item>
      <el-form-item prop="code">
        <el-input
          @focus="toogleReadonly('focus')"
          @blur="toogleReadonly('blur')"
          :readonly="isReadonly"
          placeholder="工号"
          v-model="form.code"
          ref="code"
          clearable
          maxlength="20"
          @keyup.enter.native="focus('password')"
        />
      </el-form-item>
      <el-form-item prop="password">
        <!-- 统一使用原生密码框：macOS 上原生 type="password" 会强制禁用输入法，
             避免搜狗/微信输入法 Caps Lock 切大写英文导致密码错乱。
             浏览器密码管理器不会对 readonly 输入框弹"密码建议/自动填充"下拉，
             所以失焦时保持只读，点击/聚焦/清空时再加锁，
             键入或粘贴瞬间立即解锁（默认动作在事件处理后执行，不丢字符）。
             注意：autocomplete 不能用 "new-password"，那会触发 Chrome 的
             "建议高强度密码"生成器弹框；用 "off" 即可 -->
        <el-input
          placeholder="密码"
          v-model="form.password"
          ref="password"
          :type="passwordVisible ? 'text' : 'password'"
          maxlength="32"
          autocomplete="off"
          :readonly="passwordReadonly"
          @mousedown.native="onPasswordMouseDown"
          @focus="onPasswordFocus"
          @blur="onPasswordBlur"
          @input="onPasswordInput"
          @keydown.native="unlockPasswordNow"
          @paste.native="unlockPasswordNow"
          @keyup.enter.native="onSubmit"
        >
          <!-- 自定义小眼睛: 不用内置 show-password, 因为内置图标在 readonly 状态会自动隐藏 -->
          <template #suffix>
            <i
              class="el-input__icon el-icon-view"
              :style="{ color: passwordVisible ? '#0296ff' : '', cursor: 'pointer' }"
              @mousedown.prevent
              @click="togglePasswordVisible"
            />
          </template>
        </el-input>
      </el-form-item>
    </el-form>
    <!-- 登录按钮 -->
    <el-button
      class="button"
      type="primary"
      @click="onSubmit"
      :loading="isLoading"
    >
      {{ isLoading ? "登录中" : "登 录" }}
    </el-button>
    <!-- 忘记密码提示 -->
    <el-tooltip class="item" effect="dark" placement="bottom-end">
      <span class="text-button font14 N3">忘记工号密码?</span>
      <div slot="content">
        请联系您所在酒店/集团的管理员查询工号、重置密码。
      </div>
    </el-tooltip>
  </div>
</template>

<script>
import { getUserInfo } from "@/api/dataService";
export default {
  data() {
    return {
      isLoading: false, // 是否登录中状态
      form: {
        companyName: "", // 集团名称
        code: "", // 用户工号
        password: "", // 密码
      },
      rules: {},
      isReadonly: !(this.$ENV == "trial"),
      passwordReadonly: true, // 密码框初始只读, 规避浏览器密码建议/自动填充弹框
      passwordUnlockTimer: null, // 解锁定时器句柄
      passwordVisible: false, // 密码是否明文显示
    };
  },

  mounted() {
    try {
      const companyHistory = JSON.parse(localStorage.companyHistory) || []; // 读取原有的集团历史记录
      if (companyHistory.length > 0) {
        this.form.companyName = companyHistory[0];
      }
    } catch (err) {
      localStorage.companyHistory = [];
    }
  },

  methods: {
    // 自动聚焦输入框
    focus(fieldName) {
      this.$nextTick(() => this.$refs[fieldName].focus());
    },
    // 提交验证
    async onSubmit() {
      if (this.isLoading) return false; // 阻止重复登录
      this.rules = {
        companyName: this.$getFormRule({ name: "集团名称", min: 2 }),
        code: this.$getFormRule({ name: "员工工号" }),
        password: this.$getFormRule({
          name: "密码",
          min: 6,
          minMsg: "密码长度不符合要求",
        }),
      };
      this.$nextTick(() => {
        this.$refs.form.validate(async (valid) => {
          if (!valid) return false;
          this.onPasswordLogin();
        });
      });
    },

    // 工号密码登录
    async onPasswordLogin() {
      const form = JSON.parse(JSON.stringify(this.form));
      const timestamp = new Date().getTime();
      form.password = this.$getRsaCode(timestamp + form.password); // 使用公钥加密密码
      this.isLoading = true;
      try {
        const [res, err] = await this.$post("login", { timestamp, ...form }); // 请求账密登录
        if (err) {
          this.isLoading = false;
          return this.$toast(err);
        }
        const companyHistory = JSON.parse(localStorage.companyHistory || "[]"); // 读取原有的集团历史记录
        companyHistory.unshift(this.form.companyName); // 本次登录的集团排在第一个
        localStorage.companyHistory = JSON.stringify(
          Array.from(new Set(companyHistory))
        ); // 去重后保存新历史记录
        const [user, msg] = await getUserInfo();
        if (msg) return this.$toast(msg);
        this.$emit("ok", user);
      } catch (error) {
        this.isLoading = false;
        localStorage.companyHistory = [];
      }
    },

    // 输入框发生改变触发
    handleInput(e) {
      this.$aegis.reportEvent({
        name: "手动输入集团名称",
        ext1: {
          code: this.form.code,
          companyName: e,
          localName: localStorage.companyHistory,
        },
      });
    },

    // 展示集团名称历史记录
    getHistoryCompanyList(queryString, cb) {
      const companyHistory = JSON.parse(localStorage.companyHistory || "[]"); // 读取原有的集团历史记录
      const list = companyHistory.map((e) => ({ name: e }));
      if (list.length) list[0].note = "上次使用";
      cb(list.filter((e) => e.name.includes(queryString))); // 根据关键词筛选
    },
    // 在历史记录中选择一个集团名称
    chooseCompany(item) {
      this.form.companyName = item.name;
    },

    // 切换输入框只读状态
    toogleReadonly(type) {
      if (this.$ENV == "trial") return false;
      this.isReadonly = type !== "focus";
    },

    // 屏蔽浏览器密码管理器弹框(密码建议/自动填充): 浏览器不会对 readonly 输入框弹下拉。
    // 策略: 失焦时保持只读; 点击/聚焦/清空时加锁; 键入/粘贴瞬间立即解锁;
    // 兜底定时器 300ms 后解锁, 保证不点击只聚焦等场景也能输入
    armPasswordReadonly() {
      this.passwordReadonly = true;
      this.schedulePasswordUnlock();
    },
    // 点击输入框本体才加锁; 点右侧小眼睛时跳过, 避免影响图标交互
    onPasswordMouseDown(event) {
      if (event.target && event.target.closest && event.target.closest(".el-input__suffix")) return;
      this.armPasswordReadonly();
    },
    schedulePasswordUnlock() {
      clearTimeout(this.passwordUnlockTimer);
      // 留足时间让浏览器完成"聚焦是否弹建议"的判定(该判定是异步的)
      this.passwordUnlockTimer = setTimeout(() => {
        this.passwordReadonly = false;
      }, 300);
    },
    // 键入/粘贴瞬间解锁: keydown 的默认动作(插入字符)在事件处理之后执行, 不会丢字符
    unlockPasswordNow() {
      clearTimeout(this.passwordUnlockTimer);
      this.passwordReadonly = false;
    },
    onPasswordFocus() {
      this.armPasswordReadonly();
    },
    // 失焦后恢复只读, 下次点击同样不会弹建议框
    onPasswordBlur() {
      clearTimeout(this.passwordUnlockTimer);
      this.passwordReadonly = true;
    },
    // 清空密码时浏览器会重新弹建议框, 重新加锁
    onPasswordInput(value) {
      if (!value) this.armPasswordReadonly();
    },

    // 切换密码显隐(自定义小眼睛)
    togglePasswordVisible() {
      this.passwordVisible = !this.passwordVisible;
      // 切回密文时重新加锁, 避免浏览器重新弹建议框
      if (!this.passwordVisible) this.armPasswordReadonly();
      this.focus("password"); // 保持焦点
    },
  },
};
</script>

<style scoped>
@import "../../../styles/login-form.scss";
</style>
